import { useCallback, useEffect, useMemo, useState } from "react";
import { addMonths, eachDayOfInterval, endOfDay, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isToday, startOfMonth, startOfWeek } from "date-fns";
import { ms } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import DayPanel, { eventTone } from "@/components/DayPanel";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Isn", "Sel", "Rab", "Kha", "Jum", "Sab", "Ahd"];
const dayKey = (d) => format(d, "yyyy-MM-dd");

export default function CalendarPage() {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selected, setSelected] = useState(new Date());
  const [events, setEvents] = useState([]);

  const days = useMemo(() => eachDayOfInterval({ start: startOfWeek(month, { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) }), [month]);

  const load = useCallback(async () => {
    const { data } = await api.get("/calendar", { params: { start: days[0].toISOString(), end: endOfDay(days[days.length - 1]).toISOString() } });
    setEvents(data);
  }, [days]);

  useEffect(() => { load().catch(() => setEvents([])); }, [load]);

  const byDay = useMemo(() => events.reduce((acc, e) => { (acc[dayKey(new Date(e.at))] ||= []).push(e); return acc; }, {}), [events]);

  return (
    <div>
      <PageHeader eyebrow="Kalendar" title={format(month, "MMMM yyyy", { locale: ms })} desc="Semua post terjadual dalam satu paparan. Garis putus-putus = ulangan akan datang."
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="icon" onClick={() => setMonth(addMonths(month, -1))} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="secondary" onClick={() => { setMonth(startOfMonth(new Date())); setSelected(new Date()); }} data-testid="calendar-today-btn">Hari ini</Button>
            <Button variant="secondary" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        }
      />
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="panel overflow-hidden" data-testid="calendar-grid">
          <div className="grid grid-cols-7 border-b border-border">
            {WEEKDAYS.map((d) => <p key={d} className="eyebrow py-3 text-center">{d}</p>)}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const list = byDay[dayKey(d)] || [];
              return (
                <button key={d.toISOString()} onClick={() => setSelected(d)} data-testid={`calendar-day-${dayKey(d)}`}
                  className={cn("min-h-[64px] border-b border-r border-border/60 p-1.5 text-left transition-colors hover:bg-accent/40 sm:min-h-[104px] sm:p-2",
                    !isSameMonth(d, month) && "opacity-40", isSameDay(d, selected) && "bg-primary/10 ring-1 ring-inset ring-primary/60")}>
                  <span className={cn("inline-flex h-6 w-6 items-center justify-center rounded-full font-mono text-xs", isToday(d) && "bg-primary text-white")}>{format(d, "d")}</span>
                  <div className="mt-1 hidden space-y-1 sm:block">
                    {list.slice(0, 3).map((e, i) => (
                      <p key={i} className={cn("truncate rounded px-1.5 py-0.5 text-[10px] font-medium", eventTone(e))}>
                        {format(new Date(e.at), "h:mma")} {e.targets[0]?.name.replace("Contoh: ", "")}
                      </p>
                    ))}
                    {list.length > 3 && <p className="px-1 text-[10px] text-muted-foreground">+{list.length - 3} lagi</p>}
                  </div>
                  {list.length > 0 && <span className="mt-1 block h-1.5 w-1.5 rounded-full bg-primary sm:hidden" />}
                </button>
              );
            })}
          </div>
        </div>
        <DayPanel day={selected} events={byDay[dayKey(selected)] || []} onChange={load} />
      </div>
    </div>
  );
}
